import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import Home from './pages/Home';
import News from './pages/News';
import NewsDetail from './pages/NewsDetail';
import Forum from './pages/Forum';
import ForumPost from './pages/ForumPost';
import Footer from './components/Footer';
import AIRecommendation from './pages/AIRecommendation';
import ManipulationTechniques from './pages/ManipulationTechniques';
import StockAnalysis from './pages/StockAnalysis';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import { userApi } from './services/api';
import './index.css';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showMemberCenterModal, setShowMemberCenterModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [authModalType, setAuthModalType] = useState('login'); // 'login' or 'register'
  
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // 检查登录状态
    checkLoginStatus();
    
    // 监听登录事件
    const handleShowLogin = () => {
      console.log('收到showLogin事件，显示登录弹窗');
      setShowLoginModal(true);
      setShowRegisterModal(false);
      setAuthModalType('login');
    };
    
    window.addEventListener('showLogin', handleShowLogin);
    
    // 清理事件监听
    return () => {
      window.removeEventListener('showLogin', handleShowLogin);
    };
  }, []);

  const checkLoginStatus = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // 获取用户信息
        const response = await userApi.getUserInfo();
        if (response.success) {
          setIsLoggedIn(true);
          setUser(response.data);
        } else {
          // 如果获取用户信息失败，清除token
          handleLogout();
        }
      } catch (error) {
        console.error('获取用户信息失败:', error);
        handleLogout();
      }
    } else {
      setIsLoggedIn(false);
      setUser(null);
    }
  };

  const handleLoginSuccess = (userData) => {
    setIsLoggedIn(true);
    setUser(userData);
    setShowLoginModal(false);
    setShowRegisterModal(false);
    
    // 如果用户是从推荐页面登录的，登录成功后重新加载页面
    if (location.pathname === '/recommendation') {
      // 使用延迟确保状态更新后再重新加载
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsLoggedIn(false);
    setUser(null);
    navigate('/');
  };

  const handleAuthModalClose = (type) => {
    if (type === 'register') {
      setShowLoginModal(false);
      setShowRegisterModal(true);
      setAuthModalType('register');
    } else if (type === 'login') {
      setShowLoginModal(true);
      setShowRegisterModal(false);
      setAuthModalType('login');
    } else {
      setShowLoginModal(false);
      setShowRegisterModal(false);
    }
  };

  const handleShowTerms = (e) => {
    e.preventDefault();
    setShowTermsModal(true);
  };

  const handleShowPrivacy = (e) => {
    e.preventDefault();
    setShowPrivacyModal(true);
  };

  return (
    <div className="app">
      {/* 头部导航 */}
      <header className="header">
        <div className="container">
          <div className="header-content">
            <Link to="/" className="logo">AI-EDGE</Link>
            <nav className="nav-menu">
              <Link to="/stock-diagnosis" className={location.pathname === '/stock-diagnosis' ? 'active' : ''}>诊股</Link>
              <Link to="/news" className={location.pathname === '/news' ? 'active' : ''}>新闻</Link>
              <Link to="/recommendation" className={location.pathname === '/recommendation' ? 'active' : ''}>AI推荐</Link>
              <Link to="/university" className={location.pathname === '/university' ? 'active' : ''}>思维大学</Link>
              <Link to="/forum" className={location.pathname === '/forum' ? 'active' : ''}>论坛</Link>
            </nav>
            <ul className="auth-buttons">
              {isLoggedIn ? (
                <li><a href="#" onClick={(e) => { e.preventDefault(); setShowMemberCenterModal(true); }}>会员中心</a></li>
              ) : (
                <>
                  <li><a href="#" onClick={(e) => { e.preventDefault(); handleAuthModalClose('login'); }}>登录</a></li>
                  <li><a href="#" onClick={(e) => { e.preventDefault(); handleAuthModalClose('register'); }}>注册</a></li>
                </>
              )}
            </ul>
          </div>
        </div>
      </header>

      {/* 路由内容 */}
      <Routes>
        <Route path="/" element={<Navigate to="/recommendation" replace />} />
        <Route path="/analysis" element={<StockAnalysis isLoggedIn={isLoggedIn} user={user} />} />
        <Route path="/stock-diagnosis" element={<StockAnalysis isLoggedIn={isLoggedIn} user={user} stockId={600519}/>} />
        <Route path="/news" element={<News />} />
        <Route path="/news/:id" element={<NewsDetail />} />
        <Route path="/forum" element={<Forum />} />
        <Route path="/forum/:id" element={<ForumPost />} />
        <Route path="/recommendation" element={<AIRecommendation isLoggedIn={isLoggedIn} user={user} />} />
        <Route path="/university" element={
          <div className="container" style={{ padding: '50px 20px', textAlign: 'center' }}>
            <div className="section-card">
              <div className="section-header">思维大学</div>
              <div className="section-content">
                <h2>内容建设中</h2>
                <p>思维大学板块正在建设中，敬请期待...</p>
                <div style={{ margin: '30px 0' }}>
                  <img src="/images/coming-soon.png" alt="Coming Soon" style={{ maxWidth: '300px' }} />
                </div>
              </div>
            </div>
          </div>
        } />
        <Route path="/manipulation-techniques" element={<ManipulationTechniques />} />
      </Routes>

      {/* 登录弹窗 */}
      <div id="loginModal" className="modal" style={{ display: showLoginModal ? 'block' : 'none' }}>
        <div className="modal-content">
          <h2>登录</h2>
          <LoginForm 
            onLoginSuccess={handleLoginSuccess} 
            onClose={() => handleAuthModalClose()} 
            onSwitchToRegister={() => handleAuthModalClose('register')}
          />
          <button id="closeLogin" onClick={() => handleAuthModalClose()}>关闭</button>
        </div>
      </div>

      {/* 注册弹窗 */}
      <div id="registerModal" className="modal" style={{ display: showRegisterModal ? 'block' : 'none' }}>
        <div className="modal-content">
          <h2>注册账号</h2>
          <RegisterForm 
            onRegisterSuccess={handleLoginSuccess} 
            onClose={() => handleAuthModalClose()} 
            onSwitchToLogin={() => handleAuthModalClose('login')}
            onShowTerms={handleShowTerms}
            onShowPrivacy={handleShowPrivacy}
          />
          <button id="closeRegister" onClick={() => handleAuthModalClose()}>关闭</button>
          <p className="form-switch">已有账号？<a href="#" onClick={(e) => { e.preventDefault(); handleAuthModalClose('login'); }}>点击登录</a></p>
        </div>
      </div>

      {/* 用户协议弹窗 */}
      <div id="termsModal" className="modal" style={{ display: showTermsModal ? 'block' : 'none' }}>
        <div className="modal-content" style={{ maxWidth: '600px' }}>
          <h2>用户协议</h2>
          <div className="terms-text" style={{ maxHeight: '400px', overflowY: 'auto', padding: '10px', border: '1px solid #eee', marginBottom: '15px' }}>
            <p>欢迎使用AI-EDGE交易平台，本协议是您与AI-EDGE平台之间关于使用本平台服务所订立的契约。</p>
            <h3>一、服务内容</h3>
            <p>AI-EDGE平台提供基于人工智能的股票市场分析和推荐服务，包括但不限于市场趋势预测、个股推荐等。</p>
            <h3>二、用户责任</h3>
            <p>1. 用户应当遵守中华人民共和国相关法律法规。</p>
            <p>2. 用户需对使用本平台服务的行为负责，自行承担投资风险。</p>
            <p>3. 用户应妥善保管账号密码，因账号被盗用导致的损失由用户自行承担。</p>
            <h3>三、免责声明</h3>
            <p>1. 本平台提供的分析和推荐仅供参考，不构成投资建议。</p>
            <p>2. 用户应具备风险意识，自行判断投资决策。</p>
            <p>3. 因不可抗力导致的服务中断，本平台不承担责任。</p>
          </div>
          <button id="closeTerms" onClick={() => setShowTermsModal(false)}>关闭</button>
        </div>
      </div>

      {/* 隐私政策弹窗 */}
      <div id="privacyModal" className="modal" style={{ display: showPrivacyModal ? 'block' : 'none' }}>
        <div className="modal-content" style={{ maxWidth: '600px' }}>
          <h2>隐私政策</h2>
          <div className="terms-text" style={{ maxHeight: '400px', overflowY: 'auto', padding: '10px', border: '1px solid #eee', marginBottom: '15px' }}>
            <p>AI-EDGE平台重视用户隐私保护，本隐私政策说明我们如何收集、使用和保护您的个人信息。</p>
            <h3>一、信息收集</h3>
            <p>我们可能收集的信息包括：</p>
            <p>1. 注册信息：用户名、邮箱、密码等。</p>
            <p>2. 使用记录：您使用本平台的操作记录、访问时间等。</p>
            <p>3. 设备信息：设备型号、操作系统、IP地址等。</p>
            <h3>二、信息使用</h3>
            <p>我们使用收集的信息：</p>
            <p>1. 提供、维护和改进平台服务。</p>
            <p>2. 向您推送可能感兴趣的内容和服务。</p>
            <p>3. 进行数据分析，优化用户体验。</p>
            <h3>三、信息保护</h3>
            <p>我们采取多种安全措施保护您的个人信息，包括数据加密、访问控制等。</p>
          </div>
          <button id="closePrivacy" onClick={() => setShowPrivacyModal(false)}>关闭</button>
        </div>
      </div>

      {/* 会员中心弹窗 */}
      <div id="memberCenterModal" className="modal" style={{ display: showMemberCenterModal ? 'block' : 'none' }}>
        <div className="modal-content">
          <h2>个人中心</h2>
          {user && (
            <div id="userInfo">
              <p><strong>用户名：</strong> <span id="username">{user.username}</span></p>
              <p><strong>邮箱：</strong> <span id="email">{user.email}</span></p>
              <div id="subscriptionInfo">
                <h3>订阅服务</h3>
                {user.subscriptions ? (
                  <>
                    <p><strong>服务名称：</strong> <span id="serviceName">{user.level || '普通会员'}</span></p>
                    <p><strong>开始时间：</strong> <span id="startTime">{user.subscriptionStartDate || '无'}</span></p>
                    <p><strong>结束时间：</strong> <span id="endTime">{user.subscriptionEndDate || '无'}</span></p>
                    <p><strong>已购买模块：</strong></p>
                    <ul>
                      {user.subscriptions.suck && <li>吸筹阶段</li>}
                      {user.subscriptions.wash && <li>洗盘阶段</li>}
                      {user.subscriptions.rise && <li>拉升阶段</li>}
                      {user.subscriptions.sell && <li>出货阶段</li>}
                    </ul>
                  </>
                ) : (
                  <p>您尚未订阅任何服务</p>
                )}
              </div>
            </div>
          )}
          <button id="logoutBtn" onClick={handleLogout}>退出登录</button>
          <button id="closeMemberCenter" onClick={() => setShowMemberCenterModal(false)}>关闭</button>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default App;
